import { useEffect, useRef } from "react";
import api, { wsUrl } from "../lib/api";
import { logSwallow } from "../lib/log";

/**
 * useLiveRoomWebSocket — owns the personal + venue realtime channels.
 *
 * Contract (identical to original LiveRoom):
 *   - `enabled=false` → does nothing (guards against opening before presence)
 *   - Opens ONE personal user WS on /ws/user (idempotent — refs guarantee singleton)
 *   - Looks up presence.venue_id via GET /presence/me and opens ONE venue WS if inside a venue
 *   - Both sockets invoke `onVibe()` when they receive a "vibe.new" message
 *   - Both sockets are closed on unmount OR when `enabled` flips to false
 *   - Malformed WS payloads and constructor throws are logged (dev) but swallowed
 *     for the user because the 20-second poll fallback covers correctness.
 *
 * Refs (not deps):
 *   - userWsRef  / venueWsRef  — WebSocket instances
 *   - onVibeRef                 — always points at the latest `onVibe` callback
 *                                 so ws.onmessage doesn't need to be re-bound
 *                                 (prevents WS churn if caller passes a new fn ref)
 */
export function useLiveRoomWebSocket({ enabled, onVibe }) {
  const userWsRef = useRef(null);
  const venueWsRef = useRef(null);
  const onVibeRef = useRef(onVibe);
  useEffect(() => { onVibeRef.current = onVibe; }, [onVibe]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    // Personal user channel — works in venue AND ambient mode (fanned out server-side)
    try {
      const userUrl = wsUrl("/user");
      if (userUrl && !userWsRef.current) {
        const ws = new WebSocket(userUrl);
        ws.onmessage = (ev) => {
          try {
            const m = JSON.parse(ev.data);
            if (m.type === "vibe.new" && onVibeRef.current) onVibeRef.current();
          } catch (e) { logSwallow("liveroom.userws.parse", e); }
        };
        ws.onerror = (e) => logSwallow("liveroom.userws.error", e);
        userWsRef.current = ws;
      }
    } catch (e) { logSwallow("liveroom.userws.open", e); }

    // Legacy venue channel — best effort; not required for correctness
    (async () => {
      try {
        const me = await api.get("/presence/me");
        if (cancelled) return;
        const venueId = me.data?.presence?.venue_id;
        if (!venueId) return;
        const venueUrl = wsUrl(`/venue/${venueId}`);
        if (!venueUrl || venueWsRef.current) return;
        const ws = new WebSocket(venueUrl);
        ws.onmessage = (ev) => {
          try {
            const m = JSON.parse(ev.data);
            if (m.type === "vibe.new" && onVibeRef.current) onVibeRef.current();
          } catch (e) { logSwallow("liveroom.venuews.parse", e); }
        };
        ws.onerror = (e) => logSwallow("liveroom.venuews.error", e);
        venueWsRef.current = ws;
      } catch (e) { logSwallow("liveroom.venuews.open", e); }
    })();

    return () => {
      cancelled = true;
      // Close only what this effect opened; guards against double-close
      if (userWsRef.current) {
        try { userWsRef.current.close(); } catch (e) { logSwallow("liveroom.userws.close", e); }
        userWsRef.current = null;
      }
      if (venueWsRef.current) {
        try { venueWsRef.current.close(); } catch (e) { logSwallow("liveroom.venuews.close", e); }
        venueWsRef.current = null;
      }
    };
  }, [enabled]);
}
